import { Invoice, InvoiceDisplayStatus } from '../types/invoice';
import { InvoiceCustomerDetails } from '../types/customer';
import { GroupedPaymentRequestStatus } from '../types/paymentRequest';

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function localDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function normalizeDateKey(raw?: string): string | null {
  if (!raw) return null;

  const direct = raw.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(direct)) {
    return direct;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return localDateKey(parsed);
}

export function isDueDateOverdue(dueDate?: string, now: Date = new Date()): boolean {
  const dueKey = normalizeDateKey(dueDate);
  if (!dueKey) {
    return false;
  }

  // Overdue only after the due date day has fully passed in local operator time.
  return dueKey < localDateKey(now);
}

export function formatLineItemCount(count?: number): string {
  if (!Number.isFinite(count) || (count as number) <= 0) {
    return 'No line items';
  }
  if (count === 1) {
    return '1 line item';
  }
  return `${count} line items`;
}

export function normalizeInvoiceStatus(invoice: Pick<Invoice, 'status' | 'due_date'>): InvoiceDisplayStatus {
  if (invoice.status === 'paid') return 'paid';
  if (invoice.status === 'draft') return 'draft';
  if (invoice.status === 'void') return 'void';
  if (invoice.status === 'uncollectible') return 'blocked';
  if (invoice.status === 'open') {
    if (isDueDateOverdue(invoice.due_date)) {
      return 'overdue';
    }
    return 'pending payment';
  }
  return 'blocked';
}

export function groupedRequestStatusLabel(status: GroupedPaymentRequestStatus): InvoiceDisplayStatus {
  if (status === 'pending_payment') return 'pending payment';
  if (status === 'paid') return 'paid';
  if (status === 'canceled') return 'void';
  if (status === 'expired' || status === 'replaced') return 'blocked';
  return 'blocked';
}

export function statusLabel(status: InvoiceDisplayStatus): string {
  if (status === 'pending payment') return 'Pending payment';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

export function invoiceCustomerLabel(customer: InvoiceCustomerDetails): string {
  return customer.name || customer.company_name || customer.email || 'Unknown customer';
}

export function getInvoiceCustomerScope(invoice: Invoice): string {
  if (invoice.customer_id) {
    return `customer:${invoice.customer_id}`;
  }

  return `email:${(invoice.customer_details?.email || '').trim().toLowerCase()}`;
}

export function formatCurrencyCents(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount / 100);
}

export function formatDateShort(dateString?: string): string {
  if (!dateString) return 'No due date';
  return new Date(dateString).toLocaleDateString();
}
